import { Frequency } from '@vendasta/galaxy/frequency';
import {
  BillingPeriod,
  Cost,
  LineItem,
  LineItemInterface,
  Revenue,
  RevenueComponent,
  RevenuePeriod,
} from '@vendasta/sales-orders';
import { InventoryItem } from '../inventory-item';
import { ItemRow } from './interface';

export function CreateLineItemFromItemRow(item: ItemRow, currencyOverride?: string): LineItemInterface {
  if (currencyOverride && item?.retailPricing) {
    item.retailPricing.currency = currencyOverride;
  }
  return UpdateLineItemFromItemRow(new LineItem(item.pricing), item);
}

export function UpdateLineItemFromItemRow(lineItem: LineItemInterface, item: ItemRow): LineItemInterface {
  if (item?.wholesalePricing?.usesVariablePricing) {
    lineItem.cost = new Cost({
      customPrice: item.wholesalePricing.frequencyPrices[0]?.price || undefined,
    });
  }

  const originalRevenueComponents = lineItem.currentRevenue?.revenueComponents || [];
  const revenueComponents: RevenueComponent[] = [];
  if (item?.retailPricing) {
    revenueComponents.push(
      new RevenueComponent({
        value: item.retailPricing.price || undefined,
        period: convertFrequencyToRevenuePeriod(item.retailPricing.frequency) || undefined,
        isStartingRevenue: originalRevenueComponents?.[0]?.isStartingRevenue || undefined,
      }),
    );
    lineItem.currencyCode = item.retailPricing.currency ?? 'USD';
  }
  if (item?.fees?.length) {
    item.fees
      .filter((fee) => Boolean(fee.retailAmount))
      .forEach((fee) => {
        revenueComponents.push(
          new RevenueComponent({ value: fee.retailAmount || undefined, period: RevenuePeriod.ONETIME || undefined }),
        );
      });
  }
  lineItem.currentRevenue = new Revenue({
    revenueComponents: revenueComponents,
  });

  lineItem.quantity = item?.quantity;
  if (item?.billingTerm) {
    lineItem.billingPeriod = new BillingPeriod({
      startDate: item.billingTerm?.startDate,
      endDate: item.billingTerm?.endDate,
    });
  } else {
    lineItem.billingPeriod = undefined;
  }
  return lineItem;
}

export function LineItemMatchesInventoryItem(lineItem: LineItem, item: InventoryItem): boolean {
  const itemId = item?.itemId;
  if (!itemId) return false;

  if (item.type === 'package') {
    return lineItem?.packageId === itemId;
  }

  const lineItemAppId = lineItem?.appKey?.appId;
  if (item.type === 'addon') {
    return lineItemAppId === itemId;
  }

  const lineItemEdition = lineItem?.appKey?.editionId ?? '';
  const itemEdition = item?.edition?.editionId ?? '';
  return lineItemAppId === itemId && lineItemEdition === itemEdition;
}

export function LineItemPricingEqual(li1: LineItemInterface | undefined, li2: LineItemInterface | undefined): boolean {
  if (li1?.currentRevenue?.revenueComponents?.length !== li2?.currentRevenue?.revenueComponents?.length) {
    return false;
  }
  const length = li1?.currentRevenue?.revenueComponents?.length;
  if (length) {
    for (let i = 0; i < length; i++) {
      if (
        li1?.currentRevenue?.revenueComponents?.[i].value !== li2?.currentRevenue?.revenueComponents?.[i].value ||
        li1?.currentRevenue?.revenueComponents?.[i].period !== li2?.currentRevenue?.revenueComponents?.[i].period ||
        li1?.currentRevenue?.revenueComponents?.[i].isStartingRevenue !==
          li2?.currentRevenue?.revenueComponents?.[i].isStartingRevenue
      ) {
        return false;
      }
    }
  }

  return (
    li1?.currencyCode === li2?.currencyCode &&
    li1?.quantity === li2?.quantity &&
    li1?.cost?.customPrice === li2?.cost?.customPrice &&
    li1?.billingPeriod?.startDate === li2?.billingPeriod?.startDate &&
    li1?.billingPeriod?.endDate === li2?.billingPeriod?.endDate
  );
}

function convertFrequencyToRevenuePeriod(frequency: Frequency): RevenuePeriod {
  switch (frequency) {
    case Frequency.DAILY:
      return RevenuePeriod.DAILY;
    case Frequency.WEEKLY:
      return RevenuePeriod.WEEKLY;
    case Frequency.BI_WEEKLY:
      return RevenuePeriod.BIWEEKLY;
    case Frequency.ONE_TIME:
      return RevenuePeriod.ONETIME;
    case Frequency.MONTHLY:
      return RevenuePeriod.MONTHLY;
    case Frequency.YEARLY:
      return RevenuePeriod.YEARLY;
    default:
      return RevenuePeriod.ONETIME;
  }
}
